import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, MessageCircle, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { Link, useLocation } from "react-router";
import { ChatLauncher, type ChatDockExpand } from "@altek/ui/components/chat/ChatLauncher";
import { ChatList } from "@altek/ui/components/chat/ChatList";
import { Button } from "@altek/ui/components/ui/Button";
import { Menu } from "@altek/ui/components/ui/Menu";
import { useAuth } from "@/app/providers/AuthProvider";
import { useRealtimeConnection } from "@/lib/realtime/useRealtime";
import { personQuery } from "@/features/directory/api/directory.queries";
import { chatConversationsQuery, chatUnreadCountQuery } from "../api/chat.queries";
import { CHAT_PERMISSIONS } from "../api/chat.types";
import { ChatThread, ChatThreadPlaceholder } from "./ChatThread";
import { useChatDock, type ChatDockOpenRequest } from "./chat-dock-context";
import { DraftConversation } from "./DraftConversation";
import { DRAFT_CONVERSATION_ID, draftListEntry, type DraftHandoff } from "../lib/chat-draft";
import { NewConversationPane } from "./NewConversationPane";
import { useNarrowScreen } from "../hooks/useNarrowScreen";
import { useUnreadDocumentTitle } from "../hooks/useUnreadDocumentTitle";
import { useChatSearch } from "../hooks/useChatSearch";
import { useChatThread } from "../hooks/useChatThread";
import { useConversationActions } from "../hooks/useConversationActions";
import { chatErrorMessage } from "../lib/chat-errors";
import { chatLauncherHiddenOn, chatReadable } from "../lib/chat-launcher-visibility";
import { useLauncherScrollAway } from "../hooks/useLauncherScrollAway";
import {
  byRecentActivity,
  conversationEntry,
  conversationTitle,
  findDirectConversationWith,
} from "../lib/chat-map";
import { usePinnedConversations } from "../lib/chat-pins";

/**
 * The bubble in the corner of every screen.
 *
 * IT COSTS NOTHING WHILE IT IS SHUT — and that is the rule this component is written around.
 * Closed, it runs exactly ONE query: `GET /chat/unread-count`, which is the endpoint §14b says
 * the badge is for. The conversation list and the thread are `enabled: open`, so a person who
 * never opens the dock never fetches a conversation. There is no interval anywhere in this file:
 * the counter is moved by the realtime stream, and a poll would be a request per user per
 * minute, all day, for a number that usually has not changed.
 *
 * IT OPENS NO SECOND CONNECTION. `RealtimeGate` in `AppLayout` holds the tab's one `EventSource`;
 * `ChatRealtimeBridge` only subscribes to it. §13 counts CONNECTIONS per user (default 5), so a
 * component mounted on every screen opening its own stream would spend a person's whole budget
 * on one browser.
 *
 * IT IS ABSENT, NOT DISABLED, when the module is off or the key is missing — and absent on the
 * chat page itself, where a floating bubble would open a second copy of the conversation the
 * reader is already looking at, with its own draft and its own scroll position.
 */

const WIDTH_STORAGE_KEY = "altek.chat.dock.width";

/**
 * Saklanan genişlik. Okuma ve yazma TRY içinde: gizli sekmede, depolaması kapalı bir
 * tarayıcıda ya da kotası dolmuş bir profilde `localStorage` erişimin KENDİSİ fırlatıyor —
 * ve bir kenar panelinin genişliği yüzünden uygulamanın açılmaması saçma olurdu.
 */
function readStoredWidth(): string | null {
  try {
    return window.localStorage.getItem(WIDTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStoredWidth(value: string): void {
  try {
    window.localStorage.setItem(WIDTH_STORAGE_KEY, value);
  } catch {
    // Genişlik kaybolur, oturum sürer.
  }
}

const EXPAND_STORAGE_KEY = "altek.chat.dock.expand";

/**
 * Panelin ŞEKLİ de genişliği gibi CİHAZIN kararı, hesabın değil: aynı kişi masaüstünde iki
 * sütunu, dizüstünde köşedeki dar yuvayı isteyebilir.
 *
 * İki şekilden biri değilse — eski bir sürümün yazdığı değer, elle kurcalanmış depolama —
 * sessizce yok sayılıyor ve varsayılan geçerli. Saklanan bir dizgeye körü körüne güvenmek,
 * `data-expand` niteliğine anlamsız bir değer yazıp panelin bütün genişlik kurallarını
 * düşürürdü.
 */
function readStoredExpand(): ChatDockExpand | null {
  try {
    const raw = window.localStorage.getItem(EXPAND_STORAGE_KEY);
    return raw === "dock" || raw === "full" ? raw : null;
  } catch {
    return null;
  }
}

function writeStoredExpand(value: ChatDockExpand): void {
  try {
    window.localStorage.setItem(EXPAND_STORAGE_KEY, value);
  } catch {
    // Şekil kaybolur, oturum sürer.
  }
}

/** İsteğin tipi sağlayıcıda (`ChatDockProvider`); eski içe aktarımlar için buradan da veriliyor. */
export type { ChatDockOpenRequest };

/**
 * İstekle açılan yuvada odağın gideceği yazma denetimi: konuşmanın yazma kutusu ya da yeni sohbet
 * bölmesinin arama kutusu. Kitin `firstFocusable` sırasıyla aynı fikir (önce yazılan yer), ama
 * YALNIZ konuşma sütununun içinde aranıyor — geniş şekilde DOM sırasında ilk gelen, listenin
 * arama kutusu.
 */
const TYPING_CONTROL =
  'textarea:not([disabled]), input[type="search"]:not([disabled]), input[type="text"]:not([disabled])';

/**
 * YUVADAKİ ONAY KUTUSUNUN ESCAPE'İ YUVAYA ULAŞMIYOR.
 *
 * Kitin `ChatLauncher`'ı Escape'i `document` üzerinde dinliyor ve `defaultPrevented`'a bakmıyor.
 * Yuvanın «Gruptan ayrıl» onayı (Radix, belgenin gövdesine portallanmış) açıkken odak panelin
 * dışında kaldığı için tek Escape iki katmanı birden kapatıyordu: onay kutusu VE yuva (bağımsız
 * inceleme ölçtü). Radix kendi Escape'ini `document`'ın YAKALAMA evresinde zaten işledi; olay
 * burada — React ağacında onay kutusunun atasında, portalın kabında — durdurulunca `document`'ın
 * kabarcık evresindeki kit dinleyicisine hiç varmıyor. Kitin kendi iç katmanları (bahsetme
 * listesi) aynı yolu kullanıyor. Kit isteği: dinleyici `defaultPrevented` ise kapatmasın.
 */
function stopEscapeAtLayer(event: ReactKeyboardEvent): void {
  if (event.key === "Escape") event.stopPropagation();
}

/**
 * Liste sütununu açıp kapatan TEK kontrol.
 *
 * Konuşmanın üst şeridinde ikon olarak duruyor — kitin kendi başlık düğmeleriyle (genişlet,
 * kapat) aynı dil — çünkü o şerit dar ve oraya konan bir yazı, konuşmanın başlığından yer
 * çalar. Adı `aria-label`'da, durumu `aria-expanded`'da, hedefi `aria-controls`'ta: ikon tek
 * başına ekran okuyucuya hiçbir şey söylemez. İpucu balonu YOK — yalnız fareyle ortaya çıkan
 * bir açıklama, bu yuvanın kullanıldığı tablette hiç görünmez.
 *
 * Hiçbir konuşma seçili değilken aynı düğme YAZILI çiziliyor: orada üst şerit yok, düğme boş
 * durumun eylemi oluyor ve tek başına duran bir ikon «ne yapacağım» sorusunu cevaplamıyor.
 */
function ListToggleButton({
  collapsed,
  controls,
  onToggle,
  appearance = "icon",
}: {
  collapsed: boolean;
  controls: string;
  onToggle: () => void;
  appearance?: "icon" | "labelled";
}) {
  const label = collapsed ? "Kişileri göster" : "Kişileri gizle";
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;

  if (appearance === "labelled") {
    return (
      <Button
        variant="secondary"
        size="sm"
        icon={<Icon size={16} strokeWidth={2} aria-hidden="true" />}
        aria-expanded={!collapsed}
        aria-controls={controls}
        onClick={onToggle}
      >
        {label}
      </Button>
    );
  }

  return (
    <Button
      variant="quiet"
      size="sm"
      iconOnly
      aria-label={label}
      aria-expanded={!collapsed}
      aria-controls={controls}
      onClick={onToggle}
    >
      <Icon size={16} strokeWidth={2} aria-hidden="true" />
    </Button>
  );
}

/** Where the dock's own selection lives while it is open. The page owns the URL; the dock does not. */
export function ChatLauncherHost({ openRequest }: { openRequest?: ChatDockOpenRequest | null }) {
  const { permissions, session } = useAuth();
  const location = useLocation();
  const pins = usePinnedConversations();
  const queryClient = useQueryClient();
  /**
   * İstek prop'la ya da kabuğun `ChatDockProvider`'ından geliyor. Prop verilmişse (testler,
   * önizleme sahnesi) o geçerli; verilmemişse rehber çekmecesi ve rayın canlı yardım düğmesi
   * sağlayıcı üzerinden yuvayı açıyor.
   */
  const dock = useChatDock();
  /** Kabuktaki göstergenin aynı kaynağı; yuvanın başlık altı satırı için (aşağıda `subtitle`). */
  const connection = useRealtimeConnection();
  const request = openRequest !== undefined ? openRequest : (dock?.request ?? null);
  /** Prop'la gelen istek (testler) tüketilmiyor; sağlayıcınınki işlenince siliniyor. */
  const consumeRequest = openRequest !== undefined ? undefined : dock?.consume;

  const readable = chatReadable(permissions);
  const canSend = permissions.can(CHAT_PERMISSIONS.send);
  /** Sohbet sayfası ve belge düzenleyici: düğme yok (`chatLauncherHiddenOn`, tek kural). */
  const launcherHidden = chatLauncherHiddenOn(location.pathname);
  const available = readable && !launcherHidden;

  const [open, setOpen] = useState(false);
  const scrollAway = useLauncherScrollAway({ enabled: !open, pathname: location.pathname });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [term, setTerm] = useState("");
  /**
   * Panelin iki şekli: YUVA (varsayılan) ve genişletilmiş.
   *
   * Yuva artık `size="wide"` — kitin üçüncü yuva boyu: tall yuvanın YÜKSEKLİĞİ, yanına liste
   * sığan genişlik. İkisi birden bu yüzden varsayılan olabiliyor; eskiden yan yana düzen yalnız
   * genişletilmiş şekilde vardı ve o şekil 88svh, yani "hem liste hem sohbet görünsün" isteğine
   * ekranın tamamını alarak cevap veriyordu. Büyüyen tek eksen genişlik.
   *
   * Genişletilmiş şekil duruyor, başlıktaki düğmenin arkasında: uzun bir konuşmayı okumak için
   * hâlâ işe yarıyor. Seçim cihazda saklanıyor, yoksa paneli büyüten kişi her gezinmede yeniden
   * büyütürdü.
   */
  const [expand, setExpand] = useState<ChatDockExpand>(() => readStoredExpand() ?? "dock");
  /**
   * Panelin genişliği — CİHAZ tercihi, hesabın değil.
   *
   * `localStorage`, çünkü bu bir ekran kararı: aynı kişi masaüstünde geniş, dizüstünde dar
   * ister. Sunucuya yazmak, cihaz farkını yok eden bir "tercih" üretirdi.
   *
   * Kit değeri bir `clamp()` içine sarıyor, yani eski bir sürümün sakladığı genişlik körü
   * körüne uygulanmıyor — yeni sınırlara göre yeniden kırpılıyor. Bu yüzden burada doğrulama
   * yok: sınırın tek sahibi kit.
   */
  const [panelWidth, setPanelWidth] = useState<string | undefined>(
    () => readStoredWidth() ?? undefined,
  );
  /**
   * «Yeni sohbet» yuvanın İÇİNDE açılıyor: konuşmanın durduğu sütun kişi listesine dönüşüyor.
   * Eskiden yuva kapanıp `/chat?panel=yeni`'ye gidiliyordu — bir cümle yazmak için okunan
   * sayfadan kopmak demekti.
   */
  const [composing, setComposing] = useState(false);
  /** Henüz açılmamış konuşmanın karşı tarafı. Yazıldığı anda gerçek konuşmaya dönüşüyor. */
  const [draftUserId, setDraftUserId] = useState<string | null>(null);
  /**
   * Canlı yardım / rehber isteğinde «bu kişiyle konuşma var mı» kararı beklenirken: taslak
   * `pending` çiziliyor (iskelet, kapalı yazma kutusu). Sağ sütun o arada «Bir sohbet seçin»
   * diyordu — kişi bir şey seçmişti.
   */
  const [deciding, setDeciding] = useState(false);
  /** Çağıranın bildiği ad (rehber kartı); rehber cevabı gelene kadar satırda ve başlıkta. */
  const [draftName, setDraftName] = useState<string | undefined>(undefined);
  const draftPerson = useQuery(personQuery(draftUserId));
  const draftTitle = draftPerson.data?.displayName ?? draftName;
  /** Geniş şekilde listenin daraltılmış hâli. Yuvada anlamı yok, orada zaten tek sütun var. */
  const [listCollapsed, setListCollapsed] = useState(false);
  /** Daraltma düğmesinin `aria-controls` ile işaret ettiği liste sütunu. */
  const listRegionId = useId();
  /** Kitin kökü (dar şekilde panel buradan bulunuyor) ve geniş şekilde konuşma sütunu. */
  const launcherRef = useRef<HTMLDivElement>(null);
  const detailColumnRef = useRef<HTMLDivElement>(null);
  /**
   * İŞLENMİŞ SON İSTEK. Etki başka bir sebeple yeniden koştuğunda (sohbet sayfasına gidip dönmek:
   * `launcherHidden` bağımlılıkta) aynı `nonce` yeni bir istek değil. Bu kilit yokken kapatılmış yuva
   * eski istekle kendiliğinden yeniden açılıyordu. Sağlayıcıdaki `consume` ikinci kilit: kabuk
   * yeniden bağlansa bile istek geri gelmiyor.
   */
  const handledNonce = useRef<number | null>(null);
  /**
   * «Bu kişiyle konuşma var mı» kararının BELİRTECİ. Karar eşzamansız; beklerken kişi başka bir
   * konuşma seçer, yeni sohbete geçer, yuvayı kapatır ya da kimlik değişirse belirteç ilerliyor ve
   * geç gelen karar seçimi geri almıyor. Etkinin temizliğine bağlı bir bayrak değil: istek
   * tüketilince etki yeniden koşuyor (ve StrictMode'da iki kez) — o temizlik kararı iptal ederdi.
   */
  const decisionToken = useRef(0);
  /**
   * İSTEKLE AÇILAN YUVADA ODAK YAZMA KUTUSUNA GİDİYOR — yuva hazır olunca, bir kez.
   *
   * Kit programla açılan panele odak taşımıyor («Focus enters the panel only because a person
   * pressed the button»). Ama buradaki her istek bir kişinin basışı: rehberin «Sohbet başlat»'ı,
   * rayın canlı yardımı. Odak taşınmayınca rehber çekmecesi kapanıp odak listedeki satıra
   * dönüyor, açılan yuva ise ekran okuyucuya hiç bildirilmiyordu (bağımsız inceleme). Karar
   * beklenirken yazma kutusu kapalı; odak karar gelince gidiyor. Yazılacak yer yoksa (destek
   * oturumu, erişilemeyen kişi) hiçbir şeye odaklanılmıyor — kitin kuralı.
   */
  const focusOnReady = useRef(false);
  /**
   * Dar ekranda yan yana düzen YOK — panel hangi şekilde olursa olsun.
   *
   * 768 pikselin altında kit paneli ekrana yayıyor; oraya iki sütun koymak 17rem'lik bir listenin
   * yanına avuç içi kadar bir konuşma sığdırmak olurdu. Yani düzeni kullanıcının seçtiği ŞEKİL
   * değil, ekranın ölçüsü belirliyor: telefonda liste ile konuşma sırayla açılıyor.
   */
  const fullscreen = useNarrowScreen();
  const splitLayout = !fullscreen;

  // Hooks run unconditionally — the availability check decides what is RENDERED, never which
  // hooks are called, or an account whose permissions arrive a moment late would change the
  // hook order between two renders.
  const unreadQuery = useQuery(chatUnreadCountQuery({ enabled: readable }));
  // `term` is the dock's LOCAL filter and never leaves the browser: the rail is one page and
  // `ChatList` filters what it holds. See `chatConversationsQuery` for why sending it as well
  // filtered the same list twice, by two rules that disagree.
  const listQuery = useQuery(
    chatConversationsQuery({ includeArchived: false }, { enabled: available && open }),
  );
  /**
   * Taslağın kimliği SAHTE (`DRAFT_CONVERSATION_ID`): konuşma kancasına verilmiyor. Verildiğinde
   * yuva `GET /chat/conversations/<taslak>` ve mesajlarını istiyordu — sunucu 404 dönüyor, istek
   * ise hiçbir şey göstermeyecek bir sohbet için gidiyordu.
   */
  const threadId = selectedId === DRAFT_CONVERSATION_ID ? null : selectedId;
  /** A search hit opened in the dock (H6): the thread opens at that message, then forgets it. */
  const [focusMessageId, setFocusMessageId] = useState<string | null>(null);
  const clearFocus = useCallback(() => setFocusMessageId(null), []);
  const thread = useChatThread({
    conversationId: threadId,
    enabled: available && open && threadId !== null,
    focusMessageId,
    onFocusSettled: clearFocus,
  });

  /** «Mesajlar» and «Diğer sohbetler» under the dock's search box — the page's own hook (H6). */
  const search = useChatSearch({
    term,
    enabled: available && open,
    meId: thread.meId,
    loadedRows: listQuery.data?.rows ?? [],
    isPinned: pins.isPinned,
  });

  /**
   * Sessize al, arşivle, onaylı ayrıl — SAYFAYLA AYNI kanca. Yuvanın «Sessize al»'ı eskiden
   * yuvayı kapatıp sohbet sayfasına gidiyordu; artık yerinde sessize alıyor.
   */
  const actions = useConversationActions({
    meId: thread.meId,
    onLeft: (conversationId) =>
      setSelectedId((current) => (current === conversationId ? null : current)),
  });

  /** Listedeki satırın güncel hâli: menü eylemleri sessiz/arşiv durumunu oradan okuyor. */
  const rowById = (id: string) => listQuery.data?.rows.find((candidate) => candidate.id === id);

  /**
   * SEÇİM AÇIK SÜTUNU KAPATIR. Yeni sohbet sütunu seçimin ÖNÜNDE çiziliyordu: listeden bir
   * konuşmaya basmak hiçbir şey göstermiyordu. Başka bir konuşmayı seçmek taslaktan da vazgeçmek.
   */
  const select = (id: string) => {
    setSelectedId(id);
    setComposing(false);
    setFocusMessageId(null);
    if (id !== DRAFT_CONVERSATION_ID) leaveDraft();
  };

  /** A message hit: its conversation, opened at it (H6). */
  const openMessage = (hit: { id: string; conversationId: string }) => {
    select(hit.conversationId);
    setFocusMessageId(hit.id);
  };

  /** Taslaktan çık: kişi, ad, bekleyen karar. Seçimi çağıran belirliyor. */
  function leaveDraft(): void {
    decisionToken.current += 1;
    setDraftUserId(null);
    setDraftName(undefined);
    setDeciding(false);
  }

  /**
   * «YENİ KONUŞMA» TASLAKTAN DA ÇIKARIYOR.
   *
   * Çizim sırası taslağı yeni sohbet bölmesinin ÖNÜNE koyuyor; düğme yalnız `composing`'i açıyordu
   * ve taslak açıkken hiçbir şey olmuyordu. Sohbet 18'den sonra her birebir başlangıç taslak
   * olduğu için bu artık sık yaşanıyordu: rehberden ya da canlı yardımdan açılan taslakta listenin
   * «Yeni konuşma» düğmesi ölüydü (bağımsız inceleme).
   */
  const startComposing = () => {
    leaveDraft();
    setSelectedId((current) => (current === DRAFT_CONVERSATION_ID ? null : current));
    setComposing(true);
  };

  /**
   * Taslak gerçek konuşmaya dönüştü. `handoff` varsa mesaj gitmedi (konuşma arşivde): yazılan metin
   * o konuşmanın yazma kutusuna taşınıyor, engelin nedeni ve «Arşivden çıkar» orada.
   */
  const openFromDraft = (conversationId: string, handoff?: DraftHandoff) => {
    if (handoff) thread.drafts.set(conversationId, handoff.unsentText);
    leaveDraft();
    setSelectedId(conversationId);
  };

  // The counters move while the dock is shut too — that is the whole point of the badge — but the
  // subscription is no longer here: `ChatRealtimeBridge` in the shell is the one listener for
  // chat, whatever surface is open (Sohbet 10).

  /**
   * Okunmamış toplam SEKME BAŞLIĞINDA da. Yuva her ekranda bağlı — sohbet sayfasında yalnız
   * çizmiyor — o yüzden başlığı tutan yer burası; kancalar `available`'dan önce koşuyor.
   */
  useUnreadDocumentTitle(readable ? (unreadQuery.data?.total ?? 0) : 0);

  /**
   * Leaving the dock open across a navigation is fine; leaving it open onto a page that draws no
   * bubble (/chat, the document editor) is not — it would reappear open on the way out.
   */
  useEffect(() => {
    if (launcherHidden) setOpen(false);
  }, [launcherHidden]);

  /**
   * KİMLİK DEĞİŞİNCE YUVA KENDİNE GELİR.
   *
   * `AuthProvider` kimlik değiştiğinde önbelleği boşaltıyor, ama yuvanın SEÇİMİ önbellekte
   * değil bu bileşende: destek oturumuna geçen yönetici, bir önce baktığı konuşmanın kimliğini
   * taşımaya devam ederdi. O kimlik yeni kişide ya yok (404) ya da — daha kötüsü — var ve
   * bambaşka bir konuşma. Filtre de kişisel: birinin aradığı ad, ötekinin listesinde durmaz.
   *
   * Yuva ayrıca KAPANIYOR. Kimlik değiştiren kişi o an bir iletişim kutusuyla uğraşıyor; arkada
   * açık kalan ve içeriği bir anda başkasınınkiyle dolan bir panel, en hafifinden şaşırtıcı.
   */
  const identityId = session?.user.id ?? null;
  const previousIdentity = useRef<string | null>(null);
  useEffect(() => {
    const before = previousIdentity.current;
    previousIdentity.current = identityId;
    if (identityId === null || before === null || before === identityId) return;
    decisionToken.current += 1;
    focusOnReady.current = false;
    setSelectedId(null);
    setTerm("");
    setComposing(false);
    setDraftUserId(null);
    setDraftName(undefined);
    setDeciding(false);
    setOpen(false);
  }, [identityId]);

  /**
   * Kapanan yuva bir sonraki açılışta yarım kalmış bir kişi seçimiyle karşılamaz.
   *
   * Yalnız AÇIKTAN KAPALIYA geçişte: ilk bağlanışta (ve StrictMode'un ikinci koşusunda) yuva zaten
   * kapalı ve o an işlenen bir isteğin taslağını silmek isteği yutardı.
   */
  const wasOpen = useRef(open);
  useEffect(() => {
    const closedNow = wasOpen.current && !open;
    wasOpen.current = open;
    if (!closedNow) return;
    decisionToken.current += 1;
    focusOnReady.current = false;
    setComposing(false);
    /**
     * TASLAK KAPANINCA KAYBOLUYOR — istenen davranış bu: «bir şey yazmazsa, kapatıp açınca
     * görünmesin». Yazılmışsa zaten gerçek konuşmaya dönüşmüş ve sunucuda duruyor.
     */
    setDraftUserId(null);
    setDraftName(undefined);
    setDeciding(false);
    setSelectedId((current) => (current === DRAFT_CONVERSATION_ID ? null : current));
  }, [open]);

  /**
   * Dışarıdan gelen açma isteği: yuvayı aç ve o konuşmayı seç.
   *
   * `nonce` bağımlılıkta, `conversationId` değil — aynı konuşmaya ikinci kez basıldığında da
   * çalışması gereken şey bu. Sohbet sayfasındayken hiçbir şey açmıyor: orada yuva zaten yok ve
   * çağıran doğrudan sayfaya gidiyor.
   *
   * HER İSTEK BİR KEZ İŞLENİYOR (`handledNonce`) ve sağlayıcıdan siliniyor (`consume`) — sohbet
   * sayfasındayken gelmiş olsa bile: sayfadan çıkınca yuvanın kendiliğinden açılmasına yol yok.
   */
  const requestNonce = request?.nonce ?? null;
  const requestId = request?.conversationId ?? null;
  const requestDraftUserId = request?.draftUserId ?? null;
  const requestDraftName = request?.draftName;
  const meId = session?.user.id ?? null;
  useEffect(() => {
    if (requestNonce === null || handledNonce.current === requestNonce) return;
    handledNonce.current = requestNonce;
    consumeRequest?.(requestNonce);
    if (launcherHidden) return;
    focusOnReady.current = true;
    if (requestDraftUserId !== null) {
      /**
       * O kişiyle KONUŞMA VARSA taslak açmıyoruz: ikinci bir satır üretmek, kullanıcının
       * bildirdiği çiftlenmenin ta kendisi olurdu.
       *
       * KARAR LİSTE ELDEYKEN veriliyor. Kapalı yuva listeyi hiç çekmemiş olabilir (kapalıyken
       * yalnız sayaç okunuyor); eskiden karar boş listeyle veriliyordu ve canlı yardımın ilk
       * basışı var olan konuşma yerine bir taslak açıyordu — ilk mesajla sunucu var olanı
       * döndürse de listede bir an iki satır görünüyordu. `ensureQueryData` önbellekte varsa
       * istek atmıyor. Liste okunamazsa taslak açılıyor: sunucu birebirde var olanı döndürür.
       */
      decisionToken.current += 1;
      const token = decisionToken.current;
      setComposing(false);
      setDraftUserId(requestDraftUserId);
      setDraftName(requestDraftName);
      setSelectedId(DRAFT_CONVERSATION_ID);
      setDeciding(true);
      setOpen(true);
      const decide = (rows: Parameters<typeof findDirectConversationWith>[0]) => {
        if (decisionToken.current !== token) return;
        setDeciding(false);
        const existing =
          meId === null ? null : findDirectConversationWith(rows, meId, requestDraftUserId);
        if (existing !== null) {
          setDraftUserId(null);
          setDraftName(undefined);
          setSelectedId(existing);
        }
      };
      queryClient
        .ensureQueryData(chatConversationsQuery({ includeArchived: false }))
        .then((page) => decide(page.rows))
        .catch(() => decide([]));
      return;
    }
    // Konuşma ya da yeni sohbet isteği: açık bir taslak ve bekleyen kararı varsa ikisi de bitiyor.
    decisionToken.current += 1;
    setDraftUserId(null);
    setDraftName(undefined);
    setDeciding(false);
    if (requestId === null) {
      // Ne konuşma ne kişi: yuva açılıyor ve kişi seçimiyle başlıyor.
      setSelectedId(null);
      setComposing(true);
      setOpen(true);
      return;
    }
    setSelectedId(requestId);
    setComposing(false);
    setOpen(true);
    // İstek NONCE ile tetikleniyor: ad ve kimlik değişmeden aynı düğmeye ikinci basış da çalışmalı,
    // ad ya da `meId` tek başına değişince ise etki YENİDEN çalışmamalı (yuvayı kendiliğinden açardı).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestNonce, requestId, requestDraftUserId, launcherHidden]);

  const entries = useMemo(() => {
    const rows = byRecentActivity(listQuery.data?.rows ?? []);
    const real = rows.map((row) =>
      conversationEntry(row, { meId: thread.meId, pinned: pins.isPinned(row.id) }),
    );
    if (draftUserId === null) return real;
    return [draftListEntry(draftTitle), ...real];
  }, [listQuery.data, thread.meId, pins, draftUserId, draftTitle]);

  /**
   * Yeni sohbet akışının «bu kişiyle zaten konuşuyor muyum» sorusu, yüklü listeden cevaplanıyor.
   * Liste açıkken zaten çekiliyor, yani ek bir istek yok.
   */
  const existingDirectWith = (userId: string): string | null =>
    thread.meId === null
      ? null
      : findDirectConversationWith(listQuery.data?.rows ?? [], thread.meId, userId);

  /** Yeni sohbet sütununda seçilen kişi: konuşma yok, taslak açılıyor (ilk mesajla oluşur). */
  const startDraft = (person: { userId: string; displayName: string }) => {
    decisionToken.current += 1;
    /**
     * Kişiyi seçmek de bir basış: odak taslağın yazma kutusuna (9 Ekim 2026, 3. tur). Basılan satır
     * sütunla birlikte kalkıyor, odak `<body>`'ye düşüyor ve klavyeyle gelen kişi yazacağı yeri
     * baştan arıyordu.
     */
    focusOnReady.current = true;
    setDeciding(false);
    setDraftUserId(person.userId);
    setDraftName(person.displayName);
    setSelectedId(DRAFT_CONVERSATION_ID);
    setComposing(false);
  };

  /**
   * İstekle açılan yuvada odak — yuva açık ve karar verilmişken, BİR KEZ (bkz. `focusOnReady`).
   * Bağımlılık dizisi yok: yazma kutusu kararın, taslağın ve konuşmanın hangi çiziminde
   * belirdiğini önceden bilmiyoruz; bayrak kapalıyken etki hiçbir şey yapmıyor.
   */
  useEffect(() => {
    if (!focusOnReady.current || !open || deciding) return;
    focusOnReady.current = false;
    const scope = splitLayout
      ? detailColumnRef.current
      : launcherRef.current?.querySelector<HTMLElement>('[role="dialog"]');
    scope?.querySelector<HTMLElement>(TYPING_CONTROL)?.focus();
  });

  if (!available) return null;

  /**
   * Okunmamış TOPLAM sessize alınanları saymıyor — sunucu öyle veriyor (§14b, Sohbet 11): rozet
   * ve ekran okuyucu duyurusu bu yüzden sessize alınmış bir sohbetle şişmiyor.
   */
  const unread = unreadQuery.data?.total ?? 0;

  /** Konuşmanın başlık menüsü — sayfadakiyle aynı eylemler, aynı sırada. */
  const openConversation =
    selectedId !== null && selectedId !== DRAFT_CONVERSATION_ID ? thread.conversation : undefined;
  const threadMenu = openConversation ? (
    <Menu
      actions={actions.menuFor(openConversation, {
        pinned: pins.isPinned(openConversation.id),
        toggle: () => pins.toggle(openConversation.id),
      })}
    />
  ) : null;

  /**
   * Geniş şekilde başlık HEP "Sohbetler".
   *
   * Konuşmanın kendi adını `ChatPanel` zaten kendi üstünde yazıyor; panelin başlığına da
   * koymak, aynı adı iki satır arayla iki kez okutup panelin NEYİN penceresi olduğunu
   * belirsizleştiriyordu. Dar yuvada tek sütun var, orada başlık konuşmanın adı olmalı —
   * başka hiçbir yerde yazmıyor.
   */
  const panelTitle =
    !splitLayout && selectedId
      ? (conversationTitle(thread.conversation, thread.meId) ?? "Sohbet")
      : "Sohbetler";

  return (
    <ChatLauncher
      ref={launcherRef}
      // Telefonda aşağı kaydırırken çekiliyor (`useLauncherScrollAway`); stili app.css'te.
      {...(scrollAway ? { className: "chat-launcher--away" } : {})}
      open={open}
      onOpenChange={setOpen}
      label="Sohbet"
      /**
       * TELEFONDA YALNIZ İKON. Yazılı düğme dar ekranda sayfanın alt satırından geniş bir şerit
       * kaplıyor; ad `aria-label`'da duruyor, okunmamış rozeti ikonun üstünde (denetim Sohbet 21).
       */
      appearance={fullscreen ? "icon-only" : "labelled"}
      title={panelTitle}
      /**
       * CANLI BAĞLANTI YOKSA BAŞLIĞIN ALTINDA BİR SATIR. Kabuktaki gösterge aynı şeyi söylüyor,
       * ama konuşmanın ortasındaki kişi oraya bakmıyor: mesajın gelmemesi ile hiç yazılmaması
       * yuvada birbirinden ayırt edilemez. Birkaç saniyelik kopmada çıkmıyor (`degraded`).
       */
      {...(connection.degraded
        ? { subtitle: "Canlı bağlantı yok — yeni mesajlar gecikebilir" }
        : {})}
      icon={<MessageCircle size={20} strokeWidth={2} aria-hidden="true" />}
      size="wide"
      expand={expand}
      onExpandChange={(next) => {
        setExpand(next);
        writeStoredExpand(next);
      }}
      {...(panelWidth === undefined ? {} : { panelWidth })}
      onPanelWidthChange={(next) => {
        setPanelWidth(next);
        writeStoredWidth(next);
      }}
      unread={{
        count: unread,
        ...(unread > 0 ? { announcement: `Sohbette ${unread} okunmamış mesaj var.` } : {}),
      }}
      headerActions={
        /**
         * HÂLÂ BİR BAĞLANTI, AMA ARTIK BİR YAZI DEĞİL.
         *
         * `Button` bu iş için kitin kontrolü olurdu ama `<button>` çiziyor ve `href` almıyor:
         * orta tıklama ya da ⌘-tıklama ile konuşmayı yeni sekmede açmak bir yetenek, biçim değil.
         * O yüzden `<a>` kalıyor.
         *
         * ALTI ÇİZİLİ YAZI GİTTİ. Başlık şeridinde iki ikon düğmesinin (genişlet, kapat) yanında
         * duran altı çizili bir kelime, aynı hizadaki üç kontrolü üç ayrı dilde konuşturuyordu.
         * Şimdi üçü de aynı ölçüde, aynı ağırlıkta ikon; adı `aria-label` ve `title` taşıyor.
         */
        <Link
          to={selectedId ? `/chat/${selectedId}` : "/chat"}
          onClick={() => setOpen(false)}
          aria-label="Tam ekranda aç"
          title="Tam ekranda aç"
          // Telefonda 44 px: tam ekran panelde parmakla basılan bir hedef (`max-md:size-11`).
          className="inline-flex size-8 shrink-0 items-center justify-center rounded-sm text-fg-muted max-md:size-11"
        >
          <ExternalLink size={16} strokeWidth={2} aria-hidden="true" />
        </Link>
      }
    >
      {/*
        GENİŞ ŞEKİLDE İKİSİ BİRDEN, dar yuvada sırayla.

        Şikâyet açıktı: "kişiler görünürken sohbet, sohbet görünürken kişiler görünmüyor" — ve
        cevabı yan yana düzeni panelin VARSAYILAN şekli yapmak oldu. Dar yuva 400 piksel ve
        oraya iki sütun koymak ikisini de okunmaz yapar, o yüzden orada sıra düzeni duruyor.

        Liste sütunu daraltılabiliyor ve geçiş animasyonlu: `grid-template-columns` iki değer
        arasında yumuşuyor, `width` ile yapılmıyor çünkü `width: 0` içerikteki metni son
        karesine kadar sıkıştırıp titretirdi. Süre jetondan (`duration-base`), sabit bir sayı
        değil, ve `prefers-reduced-motion` altında geçiş yok — kitin kuralı.
      */}
      {splitLayout ? (
        <div
          className="relative grid h-full min-h-0 transition-[grid-template-columns] duration-base ease-out motion-reduce:transition-none"
          /**
           * İKİ UÇ DA AYNI PARÇA TÜRÜNDE — yoksa geçiş HİÇ çalışmaz.
           *
           * `0fr` ile `minmax(0, 15rem)` arasında tarayıcı ara değer üretemiyor: parça listesinin
           * türü değişince animasyon sessizce atlanıyor ve sütun bir karede kayboluyor. İlk hâli
           * buydu ve "animasyonsuz" şikâyetinin sebebi tam olarak bu. İkisini de `minmax()` ve
           * aynı birim yapınca geçiş gerçekten akıyor.
           *
           * KONUŞMA, LİSTEDEN GENİŞ: liste bir satırda ad + önizleme taşıyor, konuşma ise cümle
           * okutuyor — kalan her piksel oraya yazılıyor.
           */
          style={
            {
              gridTemplateColumns: listCollapsed
                ? "minmax(0, 0rem) minmax(0, 1fr)"
                : "minmax(0, var(--dock-list-w)) minmax(0, 1fr)",
              /**
               * SATIR DA SINIRLI — `h-full` tek başına yetmiyor.
               *
               * Tek satırlı bir ızgarada satırın öntanımlı boyu `auto`, yani İÇERİK kadar: kabın
               * `h-full`'ü kabın yüksekliğini verir ama satırın onu aşmasını engellemez. Konuşma
               * uzayınca mesaj listesi satırı şişirdi, şişen satır da yazma alanını panelin
               * ALTINA itti — 17 mesajlık bir konuşmada yazma alanının alt kenarı panelin 992
               * piksel altındaydı, yani mesaj yazacak yer ekranda yoktu. Dört mesajla hiç
               * görünmüyordu; önizlemenin fikstürü bu yüzden uzatıldı.
               *
               * `minmax(0, 1fr)`: satır kabı kadar, ve sıfıra kadar küçülebilir — `1fr` tek
               * başına yine içerik kadar bir taban taşır.
               */
              gridTemplateRows: "minmax(0, 1fr)",
              "--dock-list-w": "15rem",
            } as CSSProperties
          }
        >
          {/*
            DARALMIŞ SÜTUN `inert`. Genişliği sıfıra inen liste görünmüyor ama kutusu hâlâ
            ağaçta: `inert` olmadan klavye kullanıcısı görünmeyen bir arama kutusunun ve on
            satırın içinden geçer, ekran okuyucu da onları okumaya devam ederdi.
          */}
          {/*
            RAYIN GENİŞLİĞİ BİR JETON, VE BURADA YUVANIN ÖLÇÜSÜNE ÇEKİLİYOR.

            `chat-list.css` 1024 pikselden geniş EKRANLARDA rayı `--chat-list-w` (320px) ile
            sabitliyor — ve bunu kapsayıcıya değil PENCEREYE bakarak yapıyor. Yuvanın sütunu 240
            piksel olduğu için liste 80 piksel taşıyordu; taşan ilk şey de arama kutusuydu.

            Sınıf ezmiyoruz, JETONU yerel olarak veriyoruz: kit zaten bu ölçüyü tek bir değişkenden
            okuyor, kaskad da onu bu alt ağaçta çözüyor. Bir sonraki kit sürümünde sınıf adı
            değişse bile bu doğru kalır.
          */}
          <div
            id={listRegionId}
            inert={listCollapsed}
            /**
             * KENARI 1024'TEN SONRA KİT ÇİZİYOR, ONDAN ÖNCE BİZ.
             *
             * `chat-list.css` rayın sağ kenarını `@media (min-width: 64rem)` içinde çiziyor.
             * İki çizgi üst üste gelince ayraç 2 piksel oluyor ve ikisi de aynı renkte olduğu
             * için bu bir gölge gibi değil, kalın bir çizgi gibi okunuyordu. `lg` Tailwind'de
             * tam olarak o 64rem, yani sayı ikinci kez yazılmıyor — eşiğin kendisi paylaşılıyor.
             */
            className="min-h-0 min-w-0 overflow-hidden border-e border-border lg:border-e-0"
            style={{ "--chat-list-w": "var(--dock-list-w)" } as CSSProperties}
          >
            {/*
              SIKIŞMIYOR, KAYIYOR — bu sabit genişlikli iç katmanın tek işi bu.

              Kit rayı `max-inline-size: 100%` taşıyor (v3.11.0, taşmayı kökten çözen kural).
              Doğru bir kural ama daralan bir sütunun içinde şu yan etkisi var: sütun 240'tan
              0'a giderken `100%` de onunla küçülüyor, yani liste her karede yeniden sarıyor —
              «Henüz konuşma yok» metni animasyon boyunca şekil değiştiriyordu.

              İç katman `--dock-list-w`'ye sabit: `100%` artık DEĞİŞMEYEN bir kutuya çözülüyor.
              Dıştaki sütun `overflow-hidden` olduğu için liste yerinde durup kenardan
              kırpılıyor — panelin arkasına kayıyormuş gibi. Z-index gerekmiyor; kırpma zaten
              üstte duranı üstte bırakıyor.
            */}
            <div
              className="h-full shrink-0 overflow-hidden"
              style={{ inlineSize: "var(--dock-list-w)" }}
            >
              <ChatList
                entries={entries}
                activeId={selectedId}
                onSelect={select}
                isLoading={listQuery.isFetching && !listQuery.data}
                query={term}
                onQueryChange={setTerm}
                {...(canSend
                  ? {
                      onNewConversation: startComposing,
                    }
                  : {})}
                onTogglePin={pins.toggle}
                onToggleMute={(id) => {
                  const row = rowById(id);
                  if (row) actions.toggleMute(row);
                }}
                onToggleArchive={(id) => {
                  const row = rowById(id);
                  if (row) actions.toggleArchive(row);
                }}
                error={listQuery.error ? chatErrorMessage(listQuery.error) : null}
                onRetry={() => void listQuery.refetch()}
                {...(search ? { remoteResults: search } : {})}
                onOpenMessage={openMessage}
              />
            </div>
          </div>

          {/* `min-h-0`: ızgara çocuğunun öntanımlı en küçük boyu içeriğidir; onsuz konuşma
              paneli kısalmayı reddeder ve satırı yeniden şişirir. */}
          <div ref={detailColumnRef} className="min-h-0 min-w-0">
            {draftUserId !== null && selectedId === DRAFT_CONVERSATION_ID ? (
              /* `key`: başka bir kişinin taslağı YENİ bir örnek — metin ve gönderim kimliği taşınmaz. */
              <DraftConversation
                key={draftUserId}
                userId={draftUserId}
                meId={thread.meId}
                nameHint={draftTitle}
                pending={deciding}
                onOpened={openFromDraft}
              />
            ) : composing ? (
              <NewConversationPane
                existingWith={existingDirectWith}
                onCancel={() => setComposing(false)}
                onCreated={(conversationId) => {
                  setSelectedId(conversationId);
                  setComposing(false);
                }}
                onStartDraft={startDraft}
              />
            ) : selectedId ? (
              <ChatThread
                thread={thread}
                reserveKey="sohbet.dock.mesajlar"
                chrome="flush"
                {...(threadMenu ? { actions: threadMenu } : {})}
              />
            ) : (
              /*
                BOŞ SÜTUN DA BİR EKRAN — ve sohbet sayfasının kullandığı BOŞ DURUMUN AYNISI.
                İkinci bir "bir sohbet seçin" yazmak, aynı cümlenin iki yerde ayrı ayrı
                değişmesine açık kapı bırakırdı; çerçeve farkı bir prop.

                Liste gizliyken metin değişiyor: solda hiçbir şey yokken "soldaki listeden
                seçin" demek, okuyanı olmayan bir sütuna bakmaya gönderir — o yüzden orada
                cümle de eylem de listeyi geri getirmeyi anlatıyor.
              */
              <ChatThreadPlaceholder
                framed={false}
                {...(listCollapsed
                  ? {
                      description: "Kişi listesi gizli. Listeyi açıp bir kişi ya da grup seçin.",
                      action: (
                        <ListToggleButton
                          collapsed
                          controls={listRegionId}
                          appearance="labelled"
                          onToggle={() => setListCollapsed(false)}
                        />
                      ),
                    }
                  : {})}
              />
            )}
          </div>

          {/*
            DARALTMA DÜĞMESİ DİKİŞİN ÜSTÜNDE — kontrol ettiği şeyin YANINDA.

            Önce konuşmanın başlık şeridindeydi: panelin en sağında duran bir düğme, en solundaki
            listeyi açıp kapatıyordu. «Buton çok uzakta» şikâyeti buydu ve haklıydı — bir kontrol,
            etkisini gösterdiği yerin yanında durur.

            Kendi izini süren bir konum: sol kenarı liste sütununun genişliğine bağlı ve o değer
            ızgarayla AYNI süreyi kullanıyor, yani sütun daralırken düğme de onunla birlikte
            kayıyor. Dikey ortada, çünkü tepe zaten iki şeridin (arama kutusu, konuşma başlığı)
            işgal ettiği yer.

            Şerit `pointer-events-none`: bütün yüksekliği kaplıyor ama yalnız düğmenin kendisi
            tıklanabiliyor, yoksa görünmez bir sütun konuşmanın metnini seçilemez yapardı.
          */}
          <div
            className="pointer-events-none absolute inset-y-0 transition-[inset-inline-start] duration-base ease-out motion-reduce:transition-none"
            style={{ insetInlineStart: listCollapsed ? "0rem" : "var(--dock-list-w)" }}
          >
            <div className="pointer-events-auto absolute top-1/2 -translate-x-1/2 -translate-y-1/2">
              <ListToggleButton
                collapsed={listCollapsed}
                controls={listRegionId}
                onToggle={() => setListCollapsed((current) => !current)}
              />
            </div>
          </div>
        </div>
      ) : draftUserId !== null && selectedId === DRAFT_CONVERSATION_ID ? (
        <DraftConversation
          key={draftUserId}
          userId={draftUserId}
          meId={thread.meId}
          nameHint={draftTitle}
          pending={deciding}
          onOpened={openFromDraft}
        />
      ) : composing ? (
        /* Dar şekilde de aynı akış, yalnız sütun yok: kişi listesi konuşmanın yerini alıyor. */
        <NewConversationPane
          existingWith={existingDirectWith}
          onCancel={() => setComposing(false)}
          onCreated={(conversationId) => {
            setSelectedId(conversationId);
            setComposing(false);
          }}
          onStartDraft={startDraft}
        />
      ) : selectedId ? (
        <ChatThread
          thread={thread}
          reserveKey="sohbet.dock.mesajlar"
          chrome="flush"
          actions={
            <>
              {/* The kit's `quiet` variant, not a hand-styled `<button>`: this is an ordinary
                  secondary action and it owes the reader the same focus ring, height and press
                  feedback as every other button in the product. */}
              <Button variant="quiet" size="sm" onClick={() => setSelectedId(null)}>
                Listeye dön
              </Button>
              {threadMenu}
            </>
          }
        />
      ) : (
        <ChatList
          entries={entries}
          activeId={null}
          onSelect={select}
          isLoading={listQuery.isFetching && !listQuery.data}
          query={term}
          onQueryChange={setTerm}
          {...(canSend
            ? {
                onNewConversation: startComposing,
              }
            : {})}
          onTogglePin={pins.toggle}
          onToggleMute={(id) => {
            const row = rowById(id);
            if (row) actions.toggleMute(row);
          }}
          onToggleArchive={(id) => {
            const row = rowById(id);
            if (row) actions.toggleArchive(row);
          }}
          error={listQuery.error ? chatErrorMessage(listQuery.error) : null}
          onRetry={() => void listQuery.refetch()}
          {...(search ? { remoteResults: search } : {})}
          onOpenMessage={openMessage}
        />
      )}
      {/* Onay kutusu portallanıyor; bu kap kutu çizmiyor (`contents`), yalnız Escape'i durduruyor. */}
      <div className="contents" onKeyDown={stopEscapeAtLayer}>
        {actions.dialog}
      </div>
    </ChatLauncher>
  );
}
